import {
  Directive,
  ElementRef,
  OnDestroy,
  Renderer2
} from '@angular/core';

@Directive({
  selector: '[appResizableColumn]'
})
export class ResizableColumnDirective implements OnDestroy {

  private handle: HTMLElement;
  private startX: number;
  private startWidth: number;
  private isResizing = false;
  private columnClass: string | null = null;

  private onMouseMove: (e: MouseEvent) => void;
  private onMouseUp: (e: MouseEvent) => void;

  constructor(private el: ElementRef<HTMLElement>, private renderer: Renderer2) {
    // Apply required styles directly so we don't depend on CSS attribute selector case
    this.renderer.setStyle(this.el.nativeElement, 'position', 'relative');
    this.renderer.setStyle(this.el.nativeElement, 'overflow', 'visible');

    this.handle = this.renderer.createElement('span');
    this.renderer.setStyle(this.handle, 'position', 'absolute');
    this.renderer.setStyle(this.handle, 'top', '0');
    this.renderer.setStyle(this.handle, 'right', '0');
    this.renderer.setStyle(this.handle, 'bottom', '0');
    this.renderer.setStyle(this.handle, 'width', '6px');
    this.renderer.setStyle(this.handle, 'cursor', 'col-resize');
    this.renderer.setStyle(this.handle, 'z-index', '100');
    this.renderer.setStyle(this.handle, 'background', 'transparent');
    this.renderer.appendChild(this.el.nativeElement, this.handle);

    this.onMouseMove = this.doResize.bind(this);
    this.onMouseUp = this.stopResize.bind(this);

    this.renderer.listen(this.handle, 'mousedown', (e: MouseEvent) => this.startResize(e));
    this.renderer.listen(this.handle, 'mouseenter', () =>
      this.renderer.setStyle(this.handle, 'background', 'rgba(0,0,0,0.18)')
    );
    this.renderer.listen(this.handle, 'mouseleave', () =>
      this.renderer.setStyle(this.handle, 'background', 'transparent')
    );
  }

  private getColumnClass(): string | null {
    const classList = Array.from(this.el.nativeElement.classList);
    return classList.find(c => c.startsWith('mat-column-')) || null;
  }

  private setWidthOnAllCells(newWidth: number): void {
    if (!this.columnClass) { return; }
    const table = this.el.nativeElement.closest('mat-table') as HTMLElement
      || this.el.nativeElement.closest('.mat-table') as HTMLElement;
    if (!table) { return; }
    const cells = table.querySelectorAll<HTMLElement>(`.${this.columnClass}`);
    cells.forEach(cell => {
      this.renderer.setStyle(cell, 'flex', `0 0 ${newWidth}px`);
      this.renderer.setStyle(cell, 'min-width', `${newWidth}px`);
      this.renderer.setStyle(cell, 'max-width', `${newWidth}px`);
      this.renderer.setStyle(cell, 'width', `${newWidth}px`);
      this.renderer.setStyle(cell, 'box-sizing', 'border-box');
    });
  }

  private startResize(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isResizing = true;
    this.startX = event.pageX;
    this.startWidth = this.el.nativeElement.offsetWidth;
    this.columnClass = this.getColumnClass();
    document.addEventListener('mousemove', this.onMouseMove);
    document.addEventListener('mouseup', this.onMouseUp);
    this.renderer.addClass(document.body, 'col-resizing');
  }

  private doResize(event: MouseEvent): void {
    if (!this.isResizing) { return; }
    const newWidth = Math.max(60, this.startWidth + (event.pageX - this.startX));
    this.setWidthOnAllCells(newWidth);
  }

  private stopResize(): void {
    this.isResizing = false;
    document.removeEventListener('mousemove', this.onMouseMove);
    document.removeEventListener('mouseup', this.onMouseUp);
    this.renderer.removeClass(document.body, 'col-resizing');
  }

  ngOnDestroy(): void {
    document.removeEventListener('mousemove', this.onMouseMove);
    document.removeEventListener('mouseup', this.onMouseUp);
  }
}